import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { Select } from "antd";
import columns from "../../columns/reports/invoiceReport";
import SkeletonTable from "../../components/table/SkeletonTable";
import { GetInvoiceReportApi } from "../../api/request/report";
import { getAllServiceApi } from "../../api/request/service";
import UserAvatar from "../../components/common/UserAvatar";
import Search from "../../components/common/Search";
import View from "../../assets/View";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import YearPicker from "../../components/common/YearPicker";

const InvoiceReport = () => {
  const [invoiceList, setInvoiceList] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [filter, setFilter] = useState(null);

  const getServiceList = async () => {
    try {
      setIsLoading(true);
      const { data } = await getAllServiceApi();
      if (data?.status && data?.data?.length) {
        const services = data?.data?.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));
        setServiceList([{ value: "all", label: "All Services" }, ...services]);
        setSelectedService("all");
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getInvoiceList = async (searchText) => {
    try {
      setIsLoading(true);
      const params = {
        ...(selectedService !== "all" && { service_type_id: selectedService }),
        status: filter?.status || [],
        page: currentPage,
        search: searchText,
        year: selectedYear,
      };
      const { data } = await GetInvoiceReportApi(params);
      if (data?.status) {
        setInvoiceList(
          data?.data?.map((item) => {
            return {
              ...item,
              file: <p className="capitalize">{item?.file}</p>,
              userDetails: <UserAvatar data={item?.user} />,
              amount: `$${
                !isNaN(Number(item?.total_amount))
                  ? Number(item?.total_amount).toFixed(2)
                  : "0.00"
              }`,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.file_name}
                </div>
              ),
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              invoice_send_date: item?.invoice_send_date
                ? moment(item?.invoice_send_date).format("lll")
                : "",
              status: (
                <p
                  className={`font-bold uppercase ${
                    item?.status === "paid" ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {item?.status}
                </p>
              ),
              action: (
                <GlobalTooltip title={"View Invoice"}>
                  <a
                    href={item?.invoice_pdf || "#"}
                    target={item?.invoice_pdf && "_blank"}
                    className="font-medium text-blue-500 w-fit inline-block"
                  >
                    <View />
                  </a>
                </GlobalTooltip>
              ),
            };
          })
        );
        setTotal(data?.total_record);
      }
      setIsLoading(false);
    } catch (error) {
      setInvoiceList([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getServiceList();
  }, []);

  useEffect(() => {
    getInvoiceList();
  }, [currentPage, selectedService, selectedYear, filter]);

  const handleTableChange = (pagination, filters) => {
    setFilter(filters);
    setCurrentPage(pagination?.current);
  };

  return (
    <div className="border rounded-xl bg-white">
      <div class="py-5 px-3 sm:px-4 sm:flex items-center justify-between">
        <h2 class="title sm:mb-0 mb-2">
          Invoices {total ? `(${total})` : null}
        </h2>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <YearPicker
            value={selectedYear}
            onChange={(year) => setSelectedYear(year)}
          />

          <div className="md:w-auto w-40">
            <Search handleSearch={getInvoiceList} />
          </div>

          <Select
            placeholder="Select service"
            className="md:w-[200px] w-40"
            options={serviceList}
            value={selectedService}
            onChange={(value) => setSelectedService(value)}
          />
        </div>
      </div>

      <div className="table-radius-0 text-light mb-4">
        <SkeletonTable
          columns={columns}
          data={invoiceList}
          isLoading={isLoading}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: 10,
            showSizeChanger: false,
          }}
          handleTableChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default InvoiceReport;
